import { useEffect, useMemo, useState } from 'react'
import useSWR from 'swr'
import axios from 'axios'
import dateFormat from 'date-fns/format'
import { normalizeHousehold, normalizeHouseholdResponse, parseRecordArray } from '../utils/householdData'
import { requestSessionCheck } from '../utils/sessionStatus'
import { validateCallLogs } from '../utils/callLogData'
import { reportClientError } from '../utils/reportClientError'


const instance = axios.create({
    baseURL: `${window.location.origin}/api/v1`,
    withCredentials: true,
    xsrfCookieName: 'csrf-token'
});

const fetcher = (...args) => instance(...args).then(res => res.data).catch(error => {
    requestSessionCheck(error)
    throw error
})

const callLogsFetcher = key => instance(key).then(response => validateCallLogs(response.data, key, response)).catch(error => {
    requestSessionCheck(error)
    throw error
})
const reportedCallLogErrors = new Map()
const callLogsOptions = {
    shouldRetryOnError: error => !error.invalidCallLogsResponse && !(error.response && error.response.status === 401),
    onError: (error, key) => {
        if (!error.invalidCallLogsResponse) return
        const now = Date.now()
        if (reportedCallLogErrors.has(key) && now - reportedCallLogErrors.get(key) < 60000) return
        reportedCallLogErrors.set(key, now)
        reportClientError(error, { componentStack: `While loading ${key}` })
    },
}

const useCallogs = (recent) => {
    const key = `/callLogs/${recent ? "recent":""}`
    const { data, error, isLoading, mutate } = useSWR(key, callLogsFetcher, callLogsOptions)
    try {
        return { callLogs: data === undefined ? undefined : validateCallLogs(data, key), isLoading, isError: error, mutate }
    } catch (parseError) {
        return { callLogs: undefined, isLoading: false, isError: parseError, mutate }
    }
}

const useHouseholdCallogs = (householdId) => {
    const { data, error, isLoading } = useSWR(`households/${householdId}/callLogs`, fetcher)   
    try {
        return { callLogs: parseRecordArray(data), isLoading, isError: error }
    } catch (parseError) {
        return { callLogs: [], isLoading, isError: error || parseError }
    }
}

const useHouseholdCases = (userOpenCasesView) => {
    const { data, error, isLoading } = useSWR(`householdCases${userOpenCasesView ? '/userOpenCases':''}`, fetcher)   
    try {
        return { householdCases: parseRecordArray(data).map(hc => {
            const record = typeof hc.household === 'string' ? JSON.parse(hc.household) : hc.household
            const household = normalizeHousehold({...record, emails: hc.emails, phoneNumbers: hc.phoneNumbers})
            return {...hc, volunteers: parseRecordArray(hc.volunteers), household}
        }), isLoading, isError: error }
    } catch (parseError) {
        return { householdCases: [], isLoading, isError: error || parseError }
    }
}

const useHouseholdHouseholdCases = (householdId) => {
    const { data, error, isLoading, mutate } = useSWR(`/households/${householdId}/cases`, fetcher)   
    try {
        return { householdCases: parseRecordArray(data).map(c => ({...c, volunteers: parseRecordArray(c.volunteers)})), isLoading, isError: error, mutate }
    } catch (parseError) {
        return { householdCases: [], isLoading, isError: error || parseError, mutate }
    }
}

const useHouseholds = () => {
    const { data, error, isLoading } = useSWR(`/households/`, fetcher)
    const result = useMemo(() => {
        try {
            return { households: parseRecordArray(data).map(normalizeHousehold) }
        } catch (parseError) {
            return { households: [], parseError }
        }
    }, [data])
    return { households: result.households, isLoading, isError: error || result.parseError }
}
const useHousehold = (householdId) => {
    const { data, error, isLoading,mutate } = useSWR(`/households/${householdId}`, fetcher)   
    try {
        return { household: normalizeHouseholdResponse(data), isLoading, isError: error, mutate }
    } catch (parseError) {
        return { household: null, isLoading, isError: error || parseError, mutate }
    }
}


// --- Appointment scheduling ------------------------------------------------
// The backend is the contract here (koa/api/appointments/routes.js): every read
// endpoint returns a PLAIN ARRAY, and every list filter is optional but must be
// well formed when sent - a present-but-malformed filter is a 400
// (koa/api/appointments/filters.js), never a silently ignored value. The two
// helpers below exist so a blank filter or an unformatted Date from a picker can
// never turn into one of those 400s.

//the api wants date-only 'yyyy-MM-dd' strings, so a Date from a picker is
//normalized here instead of at every call site
const toDateParam = value => {
    if (!value) return ''
    if (value instanceof Date) return isNaN(value.getTime()) ? '' : dateFormat(value, 'yyyy-MM-dd')
    return String(value).trim()
}

//blank/undefined params are DROPPED rather than sent empty: an absent filter
//means "no constraint" server side, which is exactly what a blank one means here
const toQueryString = (params = {}) => {
    const search = new URLSearchParams()
    Object.keys(params).forEach(key => {
        const value = params[key]
        if (value === undefined || value === null || String(value).trim() === '') return
        search.append(key, String(value).trim())
    })
    const queryString = search.toString()
    return queryString ? `?${queryString}` : ''
}

// Requirements 10.1 - 10.4. Filters are `handler`, `appointmentType`, `from` and
// `to`, all optional and combined conjunctively. NOTE: there is deliberately no
// server side default date range - absent from/to means UNBOUNDED - so the page
// owning the day/week/month toggle must pass explicit dates for the range it is
// showing.
const useAppointments = (filters = {}) => {
    const {handler,appointmentType,from,to} = filters
    const query = toQueryString({handler,appointmentType,from:toDateParam(from),to:toDateParam(to)})
    const { data, error, isLoading, mutate } = useSWR(`/appointments${query}`, fetcher)
    return { appointments: data ? data : [],isLoading,isError: error, mutate }
}

// Requirement 10.5 - the full record (includes `notes` and the household /
// handler / appointmentType relations), which is what the edit form round-trips.
// A null key skips the request entirely, so an unopened panel never hits
// /appointments/undefined.
const useAppointment = (id) => {
    const { data, error, isLoading, mutate } = useSWR(id ? `/appointments/${id}` : null, fetcher)
    return { appointment: data,isLoading,isError: error, mutate }
}

// Requirements 9.1 / 9.2 - the weekly calendar beside the appointment form.
// `weekStart` may be ANY date inside the wanted week (the server snaps it down
// to that week's SUNDAY; the week runs Sunday -> Saturday), and omitting it
// defaults to the current week. Only 'scheduled' appointments come back.
const useHandlerWeekAppointments = (handlerId, weekStart) => {
    const query = toQueryString({start:toDateParam(weekStart)})
    const { data, error, isLoading, mutate } = useSWR(handlerId ? `/appointments/handler/${handlerId}/week${query}` : null, fetcher)
    return { appointments: data ? data : [],isLoading,isError: error, mutate }
}

// Requirements 11.1 - 11.4 - the household's scheduling history: no date filter,
// most recent first, and CANCELLED appointments are included (11.4 displays
// `status`).
const useHouseholdAppointments = (householdId) => {
    const { data, error, isLoading, mutate } = useSWR(householdId ? `/appointments/household/${householdId}` : null, fetcher)
    try {
        return { appointments: parseRecordArray(data), isLoading, isError: error, mutate }
    } catch (parseError) {
        return { appointments: [], isLoading, isError: error || parseError, mutate }
    }
}

// Requirements 12.1 - 12.3 - the session user's upcoming appointments (soonest
// first, capped server side). The handler is always the logged in user, so there
// is nothing to pass in.
const useMyAppointments = () => {
    const { data, error, isLoading, mutate } = useSWR(`/appointments/dashboard/my`, fetcher)
    return { appointments: data ? data : [],isLoading,isError: error, mutate }
}

//Requirement 1.2 - the appointment type select
const useAppointmentTypes = () => {
    const { data, error, isLoading, mutate } = useSWR(`/appointmentTypes`, fetcher)
    return { appointmentTypes: data ? data : [],isLoading,isError: error, mutate }
}

//Requirement 1.3 - the duration select; durations are configurable data, not a
//hard coded list, so the form reads them from the api like the types above
const useAppointmentDurations = () => {
    const { data, error, isLoading, mutate } = useSWR(`/appointmentDurations`, fetcher)
    return { appointmentDurations: data ? data : [],isLoading,isError: error, mutate }
}

// The Jewish calendar for a date range, so the appointment calendar can label
// holidays and block out the days work is not permitted on.
//
// Server side by design: @hebcal/core lives in the backend, and the same
// `worksForbidden` flag the api refuses a booking on is what the UI blocks, so the
// two cannot drift. `from`/`to` are inclusive date-only values; omitting `to`
// lets the server default to a year out.
//
// Each entry is { date, names: [...], worksForbidden, candleLighting }. Returned
// as a DATE-KEYED OBJECT because every consumer looks days up by date rather than
// iterating - see holidaysByDate in the calendar helpers.
const useJewishHolidays = (from, to) => {
    const query = toQueryString({ from: toDateParam(from), to: toDateParam(to) })
    const { data, error, isLoading, mutate } = useSWR(`/appointments/holidays${query}`, fetcher)
    return {
        holidays: data && Array.isArray(data.holidays) ? data.holidays : [],
        isLoading,
        isError: error,
        mutate,
    }
}

//Requirements 3.2 / 3.4 - active users holding the "Appointment Handler" role
const useAppointmentHandlers = () => {
    const { data, error, isLoading, mutate } = useSWR(`/appointments/handlers`, fetcher)
    return {
        handlers: data ? data : [],
        isLoading,
        isError: error,
        mutate,
        revalidateSelectableHandlers: mutate,
    }
}

//Requirement 7.1 - the configured reminder intervals (read is open to any
//authenticated user; the write verbs are admin only)
const useReminderIntervals = () => {
    const { data, error, isLoading, mutate } = useSWR(`/appointments/reminder-intervals`, fetcher)
    return { reminderIntervals: data ? data : [],isLoading,isError: error, mutate }
}

// Business hours control appointment slot availability. The read is available to
// every authenticated scheduler; management writes remain administrator-only.
const useBusinessHours = () => {
    const { data, error, isLoading, mutate } = useSWR('/appointments/business-hours', fetcher)
    return {
        businessHours: data && Array.isArray(data.records) ? data.records : [],
        isLoading,
        isError: error,
        mutate,
    }
}

// --- Appointment settings management --------------------------------------
// Management reads deliberately use keys that cannot collide with the legacy
// plain-array hooks above. Every management response is the canonical section
// envelope returned by the server.
const MANAGEMENT_KEYS = Object.freeze({
    reminderIntervals: '/appointments/reminder-intervals?management=1',
    businessHours: '/appointments/business-hours',
    appointmentTypes: '/appointmentTypes?management=1',
    appointmentDurations: '/appointmentDurations?management=1',
    generalSettings: '/appointments/settings',
    handlerMemberships: '/appointments/handler-memberships',
})

const toCanonicalManagementSection = (payload, expectedSection) => {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
        throw new TypeError('A canonical management section response is required')
    }
    if (typeof payload.section !== 'string' ||
        (expectedSection && payload.section !== expectedSection) ||
        typeof payload.versionToken !== 'string' || !payload.versionToken ||
        !Array.isArray(payload.records)) {
        throw new TypeError('The management section response is malformed')
    }
    return payload
}

// Accept either an Axios response or the [error, response] tuple produced by
// ApiContext so section controllers never need to know which form they hold.
const getCanonicalManagementResponse = (result, expectedSection) => {
    const response = Array.isArray(result) ? result[1] : result
    const payload = response && Object.prototype.hasOwnProperty.call(response, 'data')
        ? response.data
        : response
    return toCanonicalManagementSection(payload, expectedSection)
}

const extractStaleCurrentState = result => {
    const error = Array.isArray(result) ? result[0] : result
    const body = error && error.response && error.response.data
        ? error.response.data
        : error && error.data
            ? error.data
            : error
    if (!body || body.code !== 'STALE_VERSION' || !body.current) return null
    return toCanonicalManagementSection(body.current)
}

const useManagementSection = (key, expectedSection) => {
    const { data, error, isLoading, mutate } = useSWR(key, fetcher)
    const managementSection = data === undefined
        ? undefined
        : toCanonicalManagementSection(data, expectedSection)
    return {
        managementSection,
        records: managementSection ? managementSection.records : [],
        record: managementSection && managementSection.record,
        versionToken: managementSection && managementSection.versionToken,
        isLoading,
        isError: error,
        mutate,
        revalidate: mutate,
    }
}

const useManagementReminderIntervals = () => {
    const state = useManagementSection(MANAGEMENT_KEYS.reminderIntervals, 'reminderIntervals')
    return { ...state, reminderIntervals: state.records }
}

const useManagementBusinessHours = () => {
    const state = useManagementSection(MANAGEMENT_KEYS.businessHours, 'businessHours')
    return { ...state, businessHours: state.records }
}

const useManagementAppointmentTypes = () => {
    const state = useManagementSection(MANAGEMENT_KEYS.appointmentTypes, 'appointmentTypes')
    return { ...state, appointmentTypes: state.records }
}

const useManagementAppointmentDurations = () => {
    const state = useManagementSection(MANAGEMENT_KEYS.appointmentDurations, 'appointmentDurations')
    return { ...state, appointmentDurations: state.records }
}

const useManagementGeneralSettings = () => {
    const state = useManagementSection(MANAGEMENT_KEYS.generalSettings, 'generalSettings')
    return { ...state, settings: state.records }
}

const useHandlerMemberships = () => {
    const state = useManagementSection(MANAGEMENT_KEYS.handlerMemberships, 'handlerMemberships')
    return {
        ...state,
        members: state.records,
        revalidateMemberships: state.mutate,
    }
}

const useDebouncedValue = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value)
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedValue(value), delay)
        return () => clearTimeout(timer)
    }, [value, delay])
    return debouncedValue
}

const handlerCandidateKey = query => {
    const normalized = String(query === undefined || query === null ? '' : query).trim()
    return normalized
        ? `/appointments/handler-candidates?${new URLSearchParams({ q: normalized }).toString()}`
        : '/appointments/handler-candidates'
}

const useHandlerCandidates = (query = '', options = {}) => {
    const debounceMs = typeof options === 'number' ? options : options.debounceMs === undefined ? 300 : options.debounceMs
    const enabled = typeof options === 'number' ? true : options.enabled !== false
    const debouncedQuery = useDebouncedValue(query, debounceMs)
    const state = useManagementSection(enabled ? handlerCandidateKey(debouncedQuery) : null, 'handlerMemberships')
    return {
        ...state,
        candidates: state.records,
        debouncedQuery: String(debouncedQuery === undefined || debouncedQuery === null ? '' : debouncedQuery).trim(),
        revalidateCandidates: state.mutate,
    }
}

// Version tokens are opaque values. The API requires the HTTP entity-tag form
// (including quotes), and rejecting invalid values here prevents accidental
// unversioned writes.
const buildIfMatchConfig = versionToken => {
    if (typeof versionToken !== 'string' || !versionToken.trim() || versionToken.includes('"')) {
        throw new TypeError('A valid management section version token is required')
    }
    return { headers: { 'If-Match': `"${versionToken}"` } }
}

const createReminderInterval = (api, data, versionToken) =>
    api.post('/appointments/reminder-intervals', data, buildIfMatchConfig(versionToken))
const updateReminderInterval = (api, id, data, versionToken) =>
    api.put(`/appointments/reminder-intervals/${encodeURIComponent(id)}`, data, buildIfMatchConfig(versionToken))
const deleteReminderInterval = (api, id, versionToken) =>
    api.delete(`/appointments/reminder-intervals/${encodeURIComponent(id)}`, buildIfMatchConfig(versionToken))

const createBusinessHoursWindow = (api, data, versionToken) =>
    api.post('/appointments/business-hours', data, buildIfMatchConfig(versionToken))
const updateBusinessHoursWindow = (api, id, data, versionToken) =>
    api.put(`/appointments/business-hours/${encodeURIComponent(id)}`, data, buildIfMatchConfig(versionToken))
const deleteBusinessHoursWindow = (api, id, versionToken) =>
    api.delete(`/appointments/business-hours/${encodeURIComponent(id)}`, buildIfMatchConfig(versionToken))

const createAppointmentType = (api, data, versionToken) =>
    api.post('/appointmentTypes', data, buildIfMatchConfig(versionToken))
const updateAppointmentType = (api, id, data, versionToken) =>
    api.put(`/appointmentTypes/${encodeURIComponent(id)}`, data, buildIfMatchConfig(versionToken))
const deleteAppointmentType = (api, id, versionToken) =>
    api.delete(`/appointmentTypes/${encodeURIComponent(id)}`, buildIfMatchConfig(versionToken))

const createAppointmentDuration = (api, data, versionToken) =>
    api.post('/appointmentDurations', data, buildIfMatchConfig(versionToken))
const updateAppointmentDuration = (api, id, data, versionToken) =>
    api.put(`/appointmentDurations/${encodeURIComponent(id)}`, data, buildIfMatchConfig(versionToken))
const deleteAppointmentDuration = (api, id, versionToken) =>
    api.delete(`/appointmentDurations/${encodeURIComponent(id)}`, buildIfMatchConfig(versionToken))

const updateGeneralSetting = (api, key, data, versionToken) =>
    api.put(`/appointments/settings/${encodeURIComponent(key)}`, data, buildIfMatchConfig(versionToken))
const grantHandlerMembership = (api, userId, versionToken) =>
    api.post('/appointments/handler-memberships', { userId }, buildIfMatchConfig(versionToken))
const revokeHandlerMembership = (api, userId, versionToken) =>
    api.delete(`/appointments/handler-memberships/${encodeURIComponent(userId)}`, buildIfMatchConfig(versionToken))

// Mutations follow the repo convention rather than introducing a second http
// stack: the caller passes the `api` instance from useApiHttpState(), so the
// shared loader, the csrf-token cookie and the [error,response] tuple its
// interceptors produce all behave exactly like every other form in the app.
const createAppointment = (api, data) => api.post('/appointments', data)
const updateAppointment = (api, id, data) => api.put(`/appointments/${id}`, data)
const cancelAppointment = (api, id) => api.put(`/appointments/${id}/cancel`, {})


  export {useCallogs,useHouseholdCallogs,useHouseholds,useHousehold,useHouseholdCases,useHouseholdHouseholdCases,
    useAppointments,useAppointment,useHandlerWeekAppointments,useHouseholdAppointments,useMyAppointments,
    useAppointmentTypes,useAppointmentDurations,useAppointmentHandlers,useReminderIntervals,useBusinessHours,useJewishHolidays,
    useManagementReminderIntervals,useManagementBusinessHours,useManagementAppointmentTypes,
    useManagementAppointmentDurations,useManagementGeneralSettings,useHandlerMemberships,useHandlerCandidates,
    MANAGEMENT_KEYS,toCanonicalManagementSection,getCanonicalManagementResponse,extractStaleCurrentState,
    handlerCandidateKey,buildIfMatchConfig,
    createReminderInterval,updateReminderInterval,deleteReminderInterval,
    createBusinessHoursWindow,updateBusinessHoursWindow,deleteBusinessHoursWindow,
    createAppointmentType,updateAppointmentType,deleteAppointmentType,
    createAppointmentDuration,updateAppointmentDuration,deleteAppointmentDuration,
    updateGeneralSetting,grantHandlerMembership,revokeHandlerMembership,
    createAppointment,updateAppointment,cancelAppointment}
